/**
 * ============================================================================
 * TrainingTasks — Phase 4 frontend
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: list of the current user's training tasks. Each task links to the
 *          R&U Reader for the underlying document version. The list groups
 *          tasks by state (ASSIGNED / IN_PROGRESS / COMPLETED / OVERDUE).
 *
 * UX RULES:
 *   - Numeric completion_pct + overdue_count fields are rendered locale-aware
 *     via Intl.NumberFormat — server returns raw integers (i18n-safe).
 *   - OVERDUE tasks are highlighted in red with a "due X days ago" pill.
 *   - The screen DOES NOT render any quiz answer key — TRN-002 invariant.
 *
 * URS rows: TRN-001..013, I18N-001/002 (numeric formatting), MOB-010 (deep-link to R&U reader).
 * ============================================================================
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Card, CardBody, CardHeader, StatusBadge, PageHero } from '@edms/design-system';
import { useMyTrainingTasks, useTrainingStats, type TrainingTaskItem } from '../../api/hooks';
import styles from './TrainingTasks.module.css';

const STATE_ORDER: Record<string, number> = {
  OVERDUE: 0,
  ASSIGNED: 1,
  IN_PROGRESS: 2,
  COMPLETED: 3,
  SUPERSEDED: 4,
};

export function TrainingTasks() {
  const { t, i18n } = useTranslation();
  const tasksQ = useMyTrainingTasks();
  const statsQ = useTrainingStats();
  const nf = useMemo(() => new Intl.NumberFormat(i18n.language), [i18n.language]);

  const grouped = useMemo(() => {
    const items = tasksQ.data?.items ?? [];
    const sorted = [...items].sort((a, b) => {
      const sa = STATE_ORDER[a.state] ?? 99;
      const sb = STATE_ORDER[b.state] ?? 99;
      if (sa !== sb) return sa - sb;
      return (a.due_date ?? '').localeCompare(b.due_date ?? '');
    });
    return sorted;
  }, [tasksQ.data]);

  const completionPct = statsQ.data?.completion_pct ?? 0;
  const overdueCount = statsQ.data?.overdue_count ?? 0;

  return (
    <div className={styles.screen}>
      <PageHero
        eyebrow={t('training.eyebrow', { defaultValue: 'Training' })}
        title={t('training.title', { defaultValue: 'My training' })}
        subtitle={t('training.subtitle', {
          defaultValue: 'Read & Understood tasks assigned to you.',
        })}
        actions={
          <div className={styles.statsBlock}>
            <div className={styles.statCard}>
              <span className={styles.statLabel}>
                {t('training.stats.completionLabel', { defaultValue: 'Completion' })}
              </span>
              <strong className={styles.statValue}>
                {nf.format(completionPct)}%
              </strong>
            </div>
            <div className={`${styles.statCard} ${overdueCount > 0 ? styles.statOverdue : ''}`}>
              <span className={styles.statLabel}>
                {t('training.stats.overdueLabel', { defaultValue: 'Overdue' })}
              </span>
              <strong className={styles.statValue}>{nf.format(overdueCount)}</strong>
            </div>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <h2 className={styles.sectionTitle}>
            {t('training.tasks.title', { defaultValue: 'Tasks' })}{' '}
            <span className={styles.count}>({nf.format(grouped.length)})</span>
          </h2>
        </CardHeader>
        <CardBody>
          {tasksQ.isLoading && (
            <p role="status">{t('common.loading', { defaultValue: 'Loading…' })}</p>
          )}
          {tasksQ.isError && (
            <p role="alert" className={styles.error}>
              {t('training.errors.loadFailed', { defaultValue: 'Could not load training tasks.' })}
            </p>
          )}
          {!tasksQ.isLoading && grouped.length === 0 && (
            <p className={styles.empty}>
              {t('training.empty', { defaultValue: 'No training tasks assigned.' })}
            </p>
          )}
          <ul className={styles.list}>
            {grouped.map((task) => (
              <TaskRow key={task.task_id} task={task} t={t} nf={nf} />
            ))}
          </ul>
        </CardBody>
      </Card>
    </div>
  );
}

interface TaskRowProps {
  task: TrainingTaskItem;
  t: (k: string, opts?: Record<string, unknown>) => string;
  nf: Intl.NumberFormat;
}

function TaskRow({ task, t, nf }: TaskRowProps) {
  const isOverdue = task.state === 'OVERDUE';
  const isCompleted = task.state === 'COMPLETED' || task.state === 'SUPERSEDED';

  // Days until / past due — locale-aware integer formatting.
  const dueDate = task.due_date ? new Date(task.due_date) : null;
  const now = new Date();
  const daysDelta = dueDate
    ? Math.round((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <li className={`${styles.item} ${isOverdue ? styles.overdue : ''} ${isCompleted ? styles.completed : ''}`}>
      <div className={styles.itemBody}>
        <div className={styles.itemHeading}>
          <strong>{task.doc_number}</strong>{' '}
          <span className={styles.itemTitle}>{task.title}</span>
          <StatusBadge state={(task.state as any)} />
        </div>
        <span className={styles.itemMeta}>
          {dueDate && (
            <>
              {isCompleted
                ? t('training.row.completedOn', {
                    defaultValue: 'Completed {{date}}',
                    date: task.completed_at_utc?.split('T')[0],
                  })
                : isOverdue
                  ? t('training.row.overdueBy', {
                      defaultValue: 'Overdue by {{days}} days',
                      days: nf.format(Math.abs(daysDelta ?? 0)),
                    })
                  : t('training.row.dueIn', {
                      defaultValue: 'Due in {{days}} days',
                      days: nf.format(daysDelta ?? 0),
                    })}
            </>
          )}
        </span>
      </div>
      {!isCompleted && (
        <Link
          to="/r-and-u/$versionId"
          params={{ versionId: task.document_version_id }}
          className={styles.startLink}
          aria-label={t('training.row.start', {
            defaultValue: 'Start training for {{doc}}',
            doc: task.doc_number,
          })}
        >
          {t('training.row.action.start', { defaultValue: 'Start' })}
        </Link>
      )}
    </li>
  );
}
